import React, { useState, useEffect } from 'react';
import axios from 'axios';

const PropertiesPage = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('http://127.0.0.1:5000/api/admin/properties', {
        withCredentials: true,
      });

      // Handle different response formats
      const data = response.data?.data || response.data?.properties || response.data || [];
      setProperties(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching properties:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch properties');
      setProperties([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'approved':
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
      case 'inactive':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch {
      return dateString;
    }
  };

  const formatPrice = (price) => {
    if (!price) return '-';
    if (typeof price === 'number') {
      return `$${price.toLocaleString()}`;
    }
    if (typeof price === 'string' && price.includes('$')) {
      return price;
    }
    return `$${price}`;
  };

  // Use API data if available, otherwise use empty array
  const displayProperties = properties.length > 0 ? properties : [];

  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-[#03002F] font-cabinet mb-2">
            Properties
          </h2>
          <p className="text-[#282727] font-satoshi">
            Manage and view all property submissions
          </p>
        </div>
        <button
          onClick={fetchProperties}
          className="bg-[#5F5CF0] hover:bg-[#4d4bd6] text-white font-satoshi font-bold px-4 py-2 rounded-[8px] transition-colors"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-[8px] text-sm font-satoshi">
          {error}
        </div>
      )}

      <div className="bg-white rounded-[16px] shadow-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="text-[#282727] font-satoshi">Loading properties...</div>
          </div>
        ) : displayProperties.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-[#282727] font-satoshi">No properties found</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[#03002F]">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white font-satoshi uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white font-satoshi uppercase tracking-wider">
                    Address
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white font-satoshi uppercase tracking-wider">
                    Owner
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white font-satoshi uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white font-satoshi uppercase tracking-wider">
                    Beds/Baths
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white font-satoshi uppercase tracking-wider">
                    Sqft
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white font-satoshi uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white font-satoshi uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white font-satoshi uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayProperties.map((property, index) => (
                  <tr key={property.id || property._id || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-satoshi text-[#282727]">
                      #{property.id || property._id || index + 1}
                    </td>
                    <td className="px-6 py-4 text-sm font-satoshi text-[#282727]">
                      {property.address || property.propertyAddress || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-satoshi text-[#282727]">
                      {property.owner || property.name || property.ownerName || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm font-satoshi text-[#282727]">
                      <div>{property.email || '-'}</div>
                      <div className="text-xs text-gray-500">{property.phone || property.phoneNumber || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-satoshi text-[#282727]">
                      {property.beds || property.bedrooms || '-'} / {property.baths || property.bathrooms || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-satoshi text-[#282727]">
                      {property.sqft ? property.sqft.toLocaleString() : property.squareFeet ? property.squareFeet.toLocaleString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold font-satoshi text-[#282727]">
                      {formatPrice(property.price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full font-satoshi ${getStatusColor(
                          property.status
                        )}`}
                      >
                        {property.status || 'Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-satoshi text-[#282727]">
                      {formatDate(property.date || property.createdAt || property.submittedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertiesPage;

